// @tennis-matches/services/governance
// Per-user app entitlements (admin managed via SECURITY DEFINER RPC).

import { getSupabaseClient } from "../supabase/index.js";
import {
  APP_CONFIG_SCHEMA_REST_KEY,
  clearRestEndpointUnavailable,
  isAppConfigSchemaUnavailableError,
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import { rejectInvalidTenantScopeId } from "./scopeIdValidation.js";

const SCHEMA = "app_config";
const T = "app_entitlements";
const RPC_LIST_MY = "list_my_app_entitlements";
const RPC_LIST_SCOPE = "list_app_entitlements_for_scope";
const REST_KEY_RPC = "rpc:list_my_app_entitlements";
const REST_KEY_SCOPE_RPC = "rpc:list_app_entitlements_for_scope";
const REST_KEY_TABLE = "table:app_config.app_entitlements";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __db(supabase) {
  return supabase && typeof supabase.schema === "function"
    ? supabase.schema(SCHEMA)
    : supabase;
}

function __isMissingTableError(error) {
  return (
    isMissingRestResourceError(error) ||
    isAppConfigSchemaUnavailableError(error)
  );
}

function __supabaseErrorInfo(error) {
  if (!error) return null;
  return {
    message: __asStr(error?.message),
    code: __asStr(error?.code),
    details: __asStr(error?.details),
    hint: __asStr(error?.hint),
    status: Number(error?.status || 0) || null,
  };
}

/** Kanonischer UI-Key → DB-Schlüssel in app_entitlements (vgl. role_app_access RPC). */
function __appKeyForEntitlementRpc(appKey) {
  const k = __asStr(appKey).toLowerCase();
  if (k === "matchmixer" || k === "schleifchenturnier") return "schleifchen";
  if (k === "clubchamp") return "clubmeisterschaft";
  if (k === "teamdesk") return "medenspiele";
  if (k === "ballmates-ki") return "ballmates-ki";
  return k;
}

function __rpcReason(error) {
  const code = __asStr(error?.code);
  const msg = __asStr(error?.message);
  if (code === "PGRST202" || code === "42883") return "rpc-missing";
  if (code === "42501") return "permission-denied";
  if (!msg) return "rpc-error";
  if (msg.includes("not-authenticated")) return "not-authenticated";
  if (msg.includes("not-authorized")) return "not-authorized";
  if (msg.includes("missing-scope-id")) return "missing-scope";
  if (msg.includes("missing-target")) return "missing-target";
  if (msg.includes("missing-app")) return "missing-app";
  if (msg.includes("invalid-app")) return "invalid-app";
  if (msg.includes("target-not-in-scope")) return "target-not-in-scope";
  return "rpc-error";
}

function __devLog(tag, extra = {}) {
  try {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    if (typeof console === "undefined" || !console.debug) return;
    console.debug(`[app-entitlements] ${tag}`, extra);
  } catch (_) {}
}

async function __loadMyAppEntitlementsViaRpc(supabase) {
  if (isRestEndpointUnavailable(REST_KEY_RPC)) return null;
  try {
    const { data, error } = await supabase.rpc(RPC_LIST_MY);
    if (!error && Array.isArray(data)) {
      clearRestEndpointUnavailable(REST_KEY_RPC);
      __devLog("RPC list_my_app_entitlements succeeded", {
        count: data.length,
      });
      return { ok: true, entitlements: data };
    }
    if (error) {
      if (__isMissingTableError(error)) {
        markRestEndpointUnavailable(REST_KEY_RPC);
        __devLog("RPC list_my_app_entitlements missing", {
          code: error?.code,
          status: error?.status,
        });
        return null;
      }
      __devLog("RPC list_my_app_entitlements failed", {
        code: error?.code,
        message: error?.message,
      });
      return { ok: true, entitlements: [], missing: true };
    }
  } catch (e) {
    __devLog("RPC list_my_app_entitlements exception", {
      err: String(e?.message || e),
    });
    return { ok: true, entitlements: [], missing: true };
  }
  return { ok: true, entitlements: [] };
}

async function __loadMyAppEntitlementsViaSchema(supabase) {
  if (
    isRestEndpointUnavailable(REST_KEY_TABLE) ||
    isRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY)
  ) {
    return { ok: true, entitlements: [], missing: true };
  }
  const db = __db(supabase);
  const { data, error } = await db
    .from(T)
    .select(
      "id,scope,scope_id,user_id,app_key,enabled,note,created_by,created_at,updated_at",
    )
    .order("created_at", { ascending: false })
    .limit(1000);

  if (error) {
    if (__isMissingTableError(error)) {
      markRestEndpointUnavailable(REST_KEY_TABLE);
      markRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY);
      __devLog("schema app_entitlements not exposed", {
        code: error?.code,
        status: error?.status,
      });
      return { ok: true, entitlements: [], missing: true };
    }
    return {
      ok: false,
      reason: "db-error",
      error,
      errorInfo: __supabaseErrorInfo(error),
    };
  }
  __devLog("loaded entitlements via schema", {
    count: Array.isArray(data) ? data.length : 0,
  });
  return { ok: true, entitlements: Array.isArray(data) ? data : [] };
}

export async function loadMyAppEntitlementsRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const rpcRes = await __loadMyAppEntitlementsViaRpc(supabase);
  if (rpcRes?.ok) return rpcRes;

  return { ok: true, entitlements: [], missing: true };
}

export async function loadAppEntitlementsForScopeRemote(scope, scopeId) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  if (!s || !sid) return { ok: false, reason: "missing-scope" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  if (isRestEndpointUnavailable(REST_KEY_SCOPE_RPC)) {
    return { ok: true, entitlements: [], missing: true };
  }

  const { data, error } = await supabase.rpc(RPC_LIST_SCOPE, {
    p_scope: s,
    p_scope_id: sid,
  });

  if (!error) {
    return { ok: true, entitlements: Array.isArray(data) ? data : [] };
  }

  if (__isMissingTableError(error)) {
    markRestEndpointUnavailable(REST_KEY_SCOPE_RPC);
    return { ok: true, entitlements: [], missing: true };
  }

  return {
    ok: false,
    reason: "rpc-error",
    error,
    errorInfo: __supabaseErrorInfo(error),
  };
}

export async function setAppEntitlementRemote({
  scope = "tenant",
  scopeId,
  targetUserId,
  appKey,
  enabled,
  note = null,
} = {}) {
  const s = __asStr(scope) || "tenant";
  const sid = __asStr(scopeId);
  const uid = __asStr(targetUserId);
  const app = __appKeyForEntitlementRpc(appKey);
  const en = Boolean(enabled);
  const n = note == null ? null : __asStr(note);

  if (!sid) return { ok: false, reason: "missing-scope" };
  const scopeErr = rejectInvalidTenantScopeId(s, sid);
  if (scopeErr) return scopeErr;
  if (!uid) return { ok: false, reason: "missing-target" };
  if (!app) return { ok: false, reason: "missing-app" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const payload = {
    p_scope: s,
    p_scope_id: sid,
    p_target_user_id: uid,
    p_app_key: app,
    p_enabled: en,
    p_note: n,
  };
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV)
    console.log("[app-entitlements] set payload", payload);

  const { data, error } = await supabase.rpc("set_app_entitlement", payload);
  if (error)
    return {
      ok: false,
      reason: __rpcReason(error),
      error,
      errorInfo: __supabaseErrorInfo(error),
      payload,
    };
  return { ok: true, result: data || null };
}
